import React, { useState, useContext, useEffect, useMemo } from "react";
import { PageLayout } from "../components/page-layout";
import { LeaderboardContext } from "../contexts/leaderboard-context";
import { SiteVarsContext } from "../contexts/sitevars-context";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Fade, Box, Typography } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import LeaderboardTeamDialogue from "../components/leaderboard-team-dialogue";
import { LeaderboardStatusPanel } from "../components/leaderboard-status-panel";
import { MatchdayHighlights } from "../components/statistics/matchday-highlights";
import { useSiteVarsFunctions } from "../hooks/use-site-vars-functions";
import { getFixturesResource } from "../services/fixtures.service";

export const LeaderboardPage = () => {
  const { leaderboardData } = useContext(LeaderboardContext);
  const [isTeamDialogueOpen, setTeamDialogueIsOpen] = useState(false);
  const [selectedTeamData, setSelectedTeamData] = useState({});
  const { siteVarsData } = useContext(SiteVarsContext);
  const { getCurrentGameweekEvent } = useSiteVarsFunctions();

  const currentEvent = getCurrentGameweekEvent();
  const currentWeek = currentEvent?.id || siteVarsData?.game_vars?.currentWeek || 1;
  const currentWindowValue = siteVarsData?.game_vars?.currentWindow || 1;

  // Fetch current week fixtures to map team match status
  const [fixtures, setFixtures] = useState([]);
  useEffect(() => {
    let isMounted = true;
    getFixturesResource(currentWeek).then((res) => {
      if (isMounted && res.data) {
        setFixtures(res.data);
      }
    }).catch(err => console.error("Error fetching fixtures for leaderboard:", err));

    return () => { isMounted = false; };
  }, [currentWeek]);

  // Map each Premier League teamId to its current match state: 'finished', 'live', or 'upcoming'
  const teamMatchStateMap = useMemo(() => {
    const map = new Map();
    const now = Date.now();
    fixtures.forEach((fix) => {
      const kickoff = fix.kickoff_time ? new Date(fix.kickoff_time).getTime() : null;
      const hasKickedOff = kickoff && now >= kickoff;
      const isFinished = Boolean(
        fix.finished ||
        fix.finished_provisional ||
        (fix.minutes >= 90 && fix.started) ||
        (kickoff && now - kickoff >= 115 * 60 * 1000)
      );
      const isLive = (fix.started || hasKickedOff) && !isFinished;

      let state = 'upcoming';
      if (isFinished) {
        state = 'finished';
      } else if (isLive) {
        state = 'live';
      }
      map.set(fix.team_h, state);
      map.set(fix.team_a, state);
    });
    return map;
  }, [fixtures]);

  // Leader reference for gap calculations
  const leaderTotalScore = leaderboardData.length > 0 ? leaderboardData[0].totalScore : 0;
  const secondPlaceScore = leaderboardData.length > 1 ? leaderboardData[1].totalScore : 0;
  const leaderLead = leaderTotalScore - secondPlaceScore;

  // Helper to compute in-play player breakdown for a club
  const getInPlayStats = (players) => {
    if (!Array.isArray(players) || players.length === 0) {
      return { text: "", liveCount: 0 };
    }

    const activePlayers = players.filter(p => p.transfer !== "out");
    const totalCount = activePlayers.length || 11;
    let playedCount = 0;
    let liveCount = 0;
    let toPlayCount = 0;

    activePlayers.forEach((player) => {
      const state = teamMatchStateMap.get(player.teamId);
      const hasPoints = (player.eventPoints || 0) > 0;

      if (state === 'finished') {
        playedCount++;
      } else if (state === 'live') {
        liveCount++;
      } else if (hasPoints) {
        // Points scored indicates match completed or underway
        playedCount++;
      } else {
        toPlayCount++;
      }
    });

    if (liveCount > 0) {
      return {
        text: `🟢 ${liveCount} live · ${toPlayCount} left`,
        liveCount
      };
    }

    if (toPlayCount === 0 && playedCount > 0) {
      return {
        text: `${playedCount}/${totalCount} (FT)`,
        liveCount: 0
      };
    }

    return {
      text: `${playedCount}/${totalCount} · ${toPlayCount} left`,
      liveCount: 0
    };
  };

  // Helper to compute rolling form average (last 3 gameweeks)
  const getFormAvg = (row) => {
    const prevScores = Array.isArray(row.prevScores) ? row.prevScores : [];
    const recentScores = prevScores.slice(-2); // last 2 historical weeks
    const allRecent = [...recentScores, row.weekScore || 0];
    if (allRecent.length === 0) return 0;
    const avg = allRecent.reduce((sum, val) => sum + val, 0) / allRecent.length;
    return Math.round(avg);
  };

  return (
    <PageLayout>
      <LeaderboardTeamDialogue
        open={isTeamDialogueOpen}
        teamData={selectedTeamData}
        key={selectedTeamData.clubName}
        handleCloseTeamDialogue={setTeamDialogueIsOpen}
      />
      
      <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", px: { xs: 1, sm: 2 }, boxSizing: "border-box" }}>
        <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: "auto", width: "100%" }}>
          {leaderboardData.length === 0 && (
            <Fade in={true} timeout={1000}>
              <Stack spacing={0.5} sx={{ p: 2 }}>
                <Skeleton variant="rounded" width={"100%"} height={30} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
                <Skeleton variant="rounded" width={"100%"} height={45} />
              </Stack>
            </Fade>
          )}

          {leaderboardData.length > 0 && (
            <Table size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "rgba(0, 0, 0, 0.03)" }}>
                  <TableCell style={{ width: 44, minWidth: 44, padding: "6px 0 6px 6px" }}></TableCell>
                  <TableCell style={{ width: 22, minWidth: 22, padding: "6px 2px", textAlign: "center" }}></TableCell>
                  <TableCell style={{ padding: "6px 4px" }}>Club / Manager</TableCell>
                  <TableCell style={{ width: 55, minWidth: 55, padding: "6px 4px", textAlign: "right" }}>Week</TableCell>
                  <TableCell style={{ width: 50, minWidth: 50, padding: "6px 8px 6px 4px", textAlign: "right" }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaderboardData.map((row, index) => {
                  const currentPos = index + 1;
                  const posDiff = row.prevPosition ? row.prevPosition - currentPos : 0;
                  const inPlay = getInPlayStats(row.players);
                  const formAvg = getFormAvg(row);
                  const gapToLeader = leaderTotalScore - row.totalScore;

                  return (
                    <TableRow
                      key={row.managerName || row.clubId}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        cursor: "pointer",
                        backgroundColor: index === 0 ? "rgba(255, 248, 225, 0.4)" : "inherit",
                        '&:hover': {
                          backgroundColor: index === 0 ? "rgba(255, 248, 225, 0.8)" : "rgba(0, 0, 0, 0.04)"
                        }
                      }}
                      hover
                      onClick={() => {
                        setSelectedTeamData(row);
                        setTeamDialogueIsOpen(true);
                      }}
                    >
                      {/* Column 1: Club Logo */}
                      <TableCell scope="row" style={{ width: 44, minWidth: 44, padding: "4px 0 4px 6px" }}>
                        <img
                          src={`/static/img/club-logos/120x120/${row.clubId}.png`}
                          alt={row.clubName}
                          style={{ width: '38px', height: '38px', display: 'block' }}
                        />
                      </TableCell>

                      {/* Column 2: Rank & Position Mover */}
                      <TableCell
                        style={{
                          width: 22,
                          minWidth: 22,
                          padding: "4px 2px",
                          textAlign: "center",
                          verticalAlign: "middle"
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.85rem", lineHeight: 1 }}>
                            {currentPos}
                          </Typography>
                          {posDiff > 0 && (
                            <Box sx={{ display: "flex", alignItems: "center", color: "success.main", mt: 0.2 }}>
                              <ArrowDropUpIcon sx={{ fontSize: 16, m: -0.4 }} />
                              <Typography variant="caption" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
                                {posDiff}
                              </Typography>
                            </Box>
                          )}
                          {posDiff < 0 && (
                            <Box sx={{ display: "flex", alignItems: "center", color: "error.main", mt: 0.2 }}>
                              <ArrowDropDownIcon sx={{ fontSize: 16, m: -0.4 }} />
                              <Typography variant="caption" sx={{ fontSize: "0.6rem", fontWeight: "bold" }}>
                                {Math.abs(posDiff)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>

                      {/* Column 3: Club Name, Manager Name, Form & Points Gap */}
                      <TableCell component="th" scope="row" style={{ padding: "4px 4px", verticalAlign: "middle" }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.84rem", lineHeight: 1.2 }}>
                          {row.clubName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem", lineHeight: 1.2 }}>
                          {row.managerName}
                        </Typography>
                        
                        {/* Form & Gap Pill */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                          <span
                            style={{
                              fontSize: "6.5pt",
                              color: "#555",
                              backgroundColor: "rgba(0, 0, 0, 0.05)",
                              padding: "1px 4px",
                              borderRadius: "3px",
                              fontWeight: 500,
                              whiteSpace: "nowrap"
                            }}
                          >
                            Form: {formAvg} avg
                          </span>

                          {index === 0 ? (
                            <span
                              style={{
                                fontSize: "6.5pt",
                                color: "#2e7d32",
                                backgroundColor: "rgba(46, 125, 50, 0.1)",
                                padding: "1px 4px",
                                borderRadius: "3px",
                                fontWeight: 600,
                                whiteSpace: "nowrap"
                              }}
                            >
                              {leaderLead > 0 ? `+${leaderLead} lead` : "Tied 1st"}
                            </span>
                          ) : (
                            <span
                              style={{
                                fontSize: "6.5pt",
                                color: "#d32f2f",
                                backgroundColor: "rgba(211, 47, 47, 0.08)",
                                padding: "1px 4px",
                                borderRadius: "3px",
                                fontWeight: 600,
                                whiteSpace: "nowrap"
                              }}
                            >
                              -{gapToLeader} off 1st
                            </span>
                          )}
                        </Box>
                      </TableCell>

                      {/* Column 4: Week Score & In-Play Remaining */}
                      <TableCell style={{ width: 55, minWidth: 55, padding: "4px 4px", verticalAlign: "middle", textAlign: "right" }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.88rem" }}>
                          {row.weekScore}
                        </Typography>
                        {inPlay.text && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "6.5pt",
                              color: inPlay.liveCount > 0 ? "success.main" : "text.secondary",
                              fontWeight: inPlay.liveCount > 0 ? 600 : 400,
                              display: "block",
                              lineHeight: 1.1,
                              mt: 0.3,
                              whiteSpace: "nowrap"
                            }}
                          >
                            {inPlay.text}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Column 5: Total Score */}
                      <TableCell style={{ width: 50, minWidth: 50, padding: "4px 8px 4px 4px", verticalAlign: "middle", textAlign: "right" }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.9rem" }}>
                          {row.totalScore}
                        </Typography>
                        {currentWindowValue > 1 && (
                          <Typography variant="caption" sx={{ fontSize: "7pt", color: "#999", display: "block" }}>
                            {row.windowScore + row.weekScore}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Matchday Intel Highlights: Top Manager, Gameweek MVP, Title Race */}
        <MatchdayHighlights leaderboardData={leaderboardData} />

        {/* League Status & Premier League Fixtures Schedule */}
        <LeaderboardStatusPanel />
      </Box>
    </PageLayout>
  );
};
